import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom'
import { OneRosterCombinedTeamFlowSyncType, OneRosterCombineTeamListing, CoreOneRosterConfigurationCombinedTeam, CoreOneRosterConfigurationCombinedTeamSource } from "../../../../../../models/CoreOneRosterConfigurationCombinedTeam";
import { RequestState } from "../../../../../../models/RequestState";
import { GetOneRosterCombineTeamGroups, PostOneRosterCombineTeam } from "../../../../../../store/OneRoster/OneRosterCombineTeamAction";
import { FormGroup, Label, Input } from 'reactstrap';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import StatusCode from '../../../../../../util/StatusCode';
import { SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import ModalComponent from "../../../../../misc/ModalComponent";
import { useTranslation } from 'react-i18next';
import SearchableSelectInput from "../../../../../misc/SearchableSelectInput";
import { RootState } from "../../../../../../store/store";


interface OneRosterCombineTeamGroupNewProps {
    isOpen: boolean
    toggleModal: (isOpen: boolean) => void
    success: (success: boolean) => void
    disabled: boolean
}


const validation = (team: CoreOneRosterConfigurationCombinedTeam) => {
    let valid: string[] = [];
    if (team.displayName === "") {
        valid.push('displayName')
    }
    if (team.courseTitle === "") {
        valid.push('courseTitle')
    }
    if (team.flowSyncGroupId === "") {
        valid.push('flowSyncGroupId')
    }
    if (!team.sources.length) {
        valid.push('sources')
    }
    return valid;
}

const OneRosterCombineTeamGroupsNew: React.FC<OneRosterCombineTeamGroupNewProps> = (props) => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const [selectedGroups, setSelectedGroups] = useState<string[] | undefined>();
    const groups = useSelector<RootState, RequestState<OneRosterCombineTeamListing[]>>(state => state.onerosterCombineTeam.groups);
    const [team, setTeam] = useState<CoreOneRosterConfigurationCombinedTeam>({
        flowSyncType: OneRosterCombinedTeamFlowSyncType.Group,
        displayName: "",
        courseTitle: "",
        flowSyncGroupId: "",
        sources: [],
    });
    const [status, setStatus] = useState<StatusCode>(StatusCode.NONE);
    const { t } = useTranslation(['configuration', 'common']);

    const valid = validation(team);

    useEffect(() => {
        if (moduleId) {
            GetOneRosterCombineTeamGroups(moduleId)(dispatch);
        }
    }, [dispatch, moduleId]);


    const postCombinedTeam = () => {
        if (!valid.length && moduleId) {
            setStatus(StatusCode.PENDING);
            PostOneRosterCombineTeam(moduleId, team)
                .then(x => {
                    setStatus(StatusCode.COMPLETE)
                    props.success(true)
                })
                .catch(error => {
                    setStatus(StatusCode.ERROR)
                })
        }
    }

    const updateGroup = (name: string, value: string[] | null | undefined) => {
        if (value === null) {
            setSelectedGroups(undefined);
            return;
        }
        setSelectedGroups(value)

        if (value !== undefined) {
            let sources: CoreOneRosterConfigurationCombinedTeamSource[] = [];
            value.map(x => {
                sources.push({ "flowSyncGroupId": x })
            })
            setTeam({ ...team, "sources": sources });
        }
    }

    const updateTeam = (name: string, value: undefined | string | null) => {
        setTeam({ ...team, [name]: value });
    }

    const mapGroups = () => {
        let groupOptions: SearchableOptionItem<string>[] = [];
        if (groups.code === StatusCode.COMPLETE) {
            groupOptions = groups.data
                .filter(x => x.id !== team.flowSyncGroupId)
                .map(x => ({ value: x.id, label: `${x.title}, ${x.schoolUnitTitle}` }))
        }
        return groupOptions;
    };

    return (<>
        <ModalComponent
            isOpen={props.isOpen}
            toggleModal={() => props.toggleModal(!props.isOpen)}
            header={t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}
            update={postCombinedTeam}
            disabled={valid.length > 0 || props.disabled || status === StatusCode.PENDING || status === StatusCode.COMPLETE}
            confirm={t('action.ok', { ns: 'common' })}
            cancel={t('action.cancel', { ns: 'common' })}
            saveStatus={status}
        >

            <FormGroup>
                <Label for="displayName">{t('oneRosterConfiguration.combineTeam.table.displayName', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    autoComplete="off"
                    value={team.displayName}
                    onChange={(e) => updateTeam("displayName", e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="courseTitle">{t('oneRosterConfiguration.combineTeam.table.courseTitle', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="courseTitle"
                    id="courseTitle"
                    autoComplete="off"
                    value={team.courseTitle}
                    onChange={(e) => updateTeam("courseTitle", e.target.value)}
                />
            </FormGroup>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.owner', { ns: 'configuration' })}</Label>
                <SearchableSelectInput
                    defaultText={t('oneRosterConfiguration.combineTeam.select.owner', { ns: 'configuration' })}
                    name="flowSyncGroupId"
                    items={groups.code === StatusCode.COMPLETE ? groups?.data.map(g => ({ value: g.id, label: `${g.title}, ${g.schoolUnitTitle}`})) : []}
                    selected={team.flowSyncGroupId}
                    onChange={(name, value) => updateTeam(name, value)}
                    disabled={groups.code !== StatusCode.COMPLETE}
                />
            </FormGroup>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.sources', { ns: 'configuration' })}</Label>
                <MultiSelectInput
                    items={mapGroups()}
                    onChange={(name, value) => updateGroup(name, value)}
                    name='sources'
                    selected={selectedGroups}
                    defaultText={t('oneRosterConfiguration.combineTeam.select.sources', { ns: 'configuration' })}
                    disabled={groups.code !== StatusCode.COMPLETE || team?.flowSyncGroupId === ""}
                />
            </FormGroup>
        </ModalComponent>
    </>)
}

export default OneRosterCombineTeamGroupsNew;
